import type { AiCapabilityPage } from "../schema.js";
import { ALL_ROLES, LIVE, SLOT_GROUP } from "./_shared.js";

/**
 * `GROUP-BANK-ACCOUNT` (@UC0213) — đọc "chuyển tiền vào đâu?", tier `read` (QĐ PO 15/09 A4). Executor =
 * `GroupBankService.getBankAccount` (groups/group-bank.service.ts:112) — cùng hàm với màn Tài khoản nhận tiền của nhóm.
 * ĐẶT/ĐỔI tài khoản vẫn là `redirect` (0049) — trang này chỉ đọc. Người dẫn đường đính trang này trước bước "báo đã chuyển".
 *
 * 🔴 **KHÔNG ô chữ tự do, và đó là chốt PII của chính trang** (US-AIV2-0048). Câu người dùng gõ trở thành `rawPrompt` ở
 * `ai_audit_log` — bảng **append-only**, tức thứ lọt vào thì **không scrub được về sau**. Một ô `text` ở trang SỐ TÀI KHOẢN
 * là một lời mời gõ số TK vào sổ vĩnh viễn. Ô duy nhất là `nhom` (đường vào nhóm hiệu lực).
 *
 * 🔴 **KHÔNG có trục redaction theo VAI ở cửa này — đừng bịa một cái.** `groups.controller.ts:917-920-920` ghi rõ đường ĐỌC
 * *cố ý không gắn* `@RequireGroupRoles`: *"Số tài khoản nhận tiền của quỹ không phải bí mật với thành viên — họ chính là
 * người phải chuyển vào đó."* `groupBankAccountResponseSchema` vì thế **không có** nhánh `redacted` (khác
 * `collectionProgressItemSchema` của 0045). Trục thật của trang này là **rò ra LLM** (scrub ở `LlmClient`) và **trạng thái
 * rỗng**, không phải vai.
 *
 * 🔴 `facts` đổi sang `tenNhom` + `tomTat` (nháp 0013 khai bốn chỗ trống). `accountNumber`/`accountName`/`bankName` đều
 * `.optional()`; nhóm chưa cấu hình ⇒ ba field VẮNG, mà `renderTemplate` **ném** khi chỗ trống rỗng ⇒ template bốn chỗ
 * trống là một ca 500 **chắc chắn** cho mọi nhóm chưa đặt TK, và ném SAU KHI đã tốn truy vấn. Cùng bẫy `tenDot` của 0045.
 * Gộp phần thay đổi vào `tomTat` ⇒ một template phục vụ cả hai nhánh, và câu CTA "Chuyển xong…" chỉ xuất hiện ở nhánh CÓ
 * tài khoản (dán nó vào câu "chưa đặt TK" là vô nghĩa).
 */
export const GROUP_BANK_ACCOUNT = {
  id: "GROUP-BANK-ACCOUNT",
  uc: "@UC0213",
  tier: "read",
  status: LIVE,
  roles: ALL_ROLES,
  flags: [],
  slots: [SLOT_GROUP],
  llm: {
    description: "Xem thông tin tài khoản nhận tiền của nhóm (ngân hàng, số tài khoản, tên chủ tài khoản, mã QR nếu có).",
    examples: [
      "chuyển tiền vào đâu?",
      "chuyen tien vao dau",
      "số tài khoản nhận tiền quỹ",
      "so tai khoan nhan tien quy",
      "stk của thủ quỹ",
      "stk thu quy",
      "tài khoản nhận",
      "chuyển khoản vào số nào",
      "cho mình số tài khoản để đóng quỹ",
      "stk nhóm",
      "qr chuyển tiền",
      "số tk",
      // ——— 0105: thêm cho đủ 40 ———
      "so tk",
      "qr chuyen tien",
      "tai khoan nhan",
      "chuyen khoan vao so nao",
      "cho minh so tai khoan de dong quy",
      "đóng quỹ thì chuyển vào đâu",
      "dong quy thi chuyen vao dau",
      "cho xin mã qr của nhóm",
      "cho xin ma qr cua nhom",
      "nhóm nhận tiền ở ngân hàng nào",
      "nhom nhan tien o ngan hang nao",
      "tên chủ tài khoản nhận tiền là ai",
      "ten chu tai khoan nhan tien la ai",
      "xem tài khoản nhận tiền của quỹ",
      "xem tai khoan nhan tien cua quy",
      "thông tin chuyển khoản của nhóm",
      "thong tin chuyen khoan cua nhom",
      "mình chuyển tiền quỹ vào tài khoản nào vậy",
      "minh chuyen tien quy vao tai khoan nao vay",
      "gửi lại stk cho mình với",
      "gui lai stk cho minh voi",
      "quét mã qr đóng quỹ",
      "quet ma qr dong quy",
      "cho hỏi tài khoản ngân hàng của quỹ lớp",
      "cho hoi tai khoan ngan hang cua quy lop",
      "xem qr nhóm",
      "xem qr nhom",
      "nhóm nhận tiền ở tài khoản nào",
    ],
    // AI-INTERP M12 (QĐ I12): câu THỬ — KHÔNG vào prompt/BM25; viết đời thường, không chép ví dụ (validator HELD_OUT_IS_EXAMPLE).
    heldOutExamples: [
      "ck vo stk nao vay chi",
      "cho em xin số tài khoản quỹ lớp với ạ",
      "gui tui cai ma qr de ck",
      "tk ngân hàng nhóm tên ai đứng vậy",
      "đóng tiền chuyển vô đâu cô ơi",
      "stk quy doi bong la gi",
    ],
  },
  executor: "groupBank.getBankAccount",
  /**
   * 🔴 US-AIV2-0113 M03 (@UC0213 @UC0057) — màn GUI làm cùng việc "chuyển tiền vào đâu": mã QR + số TK + nội dung CK của ĐÚNG khoản
   * người hỏi còn phải đóng (`memberDueId` do binding lấy từ "khoản của tôi", server kiểm chủ khoản lần nữa khi đọc nội dung CK).
   * Nút chỉ hiện khi binding khai (`AI_FACT_GUI_SCREEN_KEY`) — đã đóng đủ / nhóm chưa cài TK ⇒ không mời chuyển tiền.
   */
  guiScreen: "/groups/:groupId/bank-qr/:memberDueId",
  screenShows: "due_transfer_info",
  /** `@UC0216` — cửa mobile trang này chịu trách nhiệm: ĐỌC tài khoản nhận tiền; ĐẶT/ĐỔI là `GRP-BANK-QR` (redirect). */
  endpoints: [
    "GET /groups/{groupId}/bank-account",
  ],
  facts: ["tenNhom", "tomTat"],
  templates: {
    // Chỉ fact được đứng ở đây: `renderReply` chỉ nhận facts của executor, nhãn ô KHÔNG có mặt (lớp đã khoá ở 0045).
    reply: "Nhóm {tenNhom} {tomTat}",
  },
  /**
   * US-AIV2-tourguide-0005 (@UC0364 · QĐ PO Q1 Q2) — ví dụ của spec §2. Mỗi nút vẫn qua cổng: «Báo đã chuyển» iff `PAY-REPORT.ready()` (Q1) ·
   * «Cài tài khoản nhận tiền» iff chủ nhóm/quản trị (vai của `GRP-BANK-QR`, Q2). Còn khoản ⇒ câu đã nói khoản ⇒ phủ thẻ `DUTY-MY-UNPAID`.
   * `dues_unknown`: hàm thuần được gọi không kèm khoản (binding không đi đường này) — mời báo như câu CTA của nó.
   */
  journey: {
    outcomes: {
      // US-AIV2-tourguide-0013 (@UC0377 · QĐ PO 06/10): nhóm CHƯA có tài khoản ⇒ nút «Cài tài khoản nhận tiền» (nhãn thẻ), không tên trang «Đổi…».
      // UX-GUIDE-POLISH MED-1 (@UC0377 · P03): câu trang ĐÃ nói "chưa đặt tài khoản" ⇒ phủ thẻ `DUTY-NO-BANK-ACCOUNT` (giữ NÚT «Cài tài khoản nhận tiền» của
      // thẻ khi nó là dòng dẫn đường — mở thẳng màn, MED-11 — bỏ câu thẻ lặp lại cùng sự thật).
      // UX-BANK-ANSWER-BUTTON (V-1 · @UC0216): nút `GRP-BANK-QR` mượn nhãn thẻ ⇒ mở THẲNG màn như nút thẻ (`outcomeBorrowedCtas` + `shows` của thẻ), và với
      // người cài được nó là nút câu trang HỨA ⇒ dòng dẫn đường KHÁC (khoản riêng · «Mở đợt thu») đứng CẠNH, không thay (`attachGuideLine` `keep`).
      // V-5 (review #1439 LOW-3): nút CÀI đứng trước «Báo đã chuyển» — nhóm chưa có chỗ nhận tiền ⇒ việc đầu của người cài được là cài (nút đầu = nút chính).
      not_configured: { next: ["GRP-BANK-QR", "PAY-REPORT"], buttonLabelFrom: { "GRP-BANK-QR": "DUTY-NO-BANK-ACCOUNT" }, covers: ["DUTY-NO-BANK-ACCOUNT"] },
      no_open_due: { next: [] },
      has_dues: { next: ["PAY-REPORT"], covers: ["DUTY-MY-UNPAID"] },
      dues_unknown: { next: ["PAY-REPORT"] },
    },
  },
  help: { title: "Xem thông tin chuyển khoản", tryNow: "chuyển tiền vào đâu?" },
} satisfies AiCapabilityPage;
